/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { Ref, forwardRef, isValidElement } from 'react';
import { useResponsiveProp } from '#hooks/use-responsive-prop.ts';
import { VisuallyHidden } from '#components/visually-hidden/visually-hidden.tsx';
import type { GDSInputLabelProps } from '../types';
import { styled } from '../../../theme';
import { labelLegendStyles } from '../utilities';
import { LabelText } from './label-text';
import { GenesisCoreInspector } from '../../../test-utils/genesis-core-inspector';

const DISPLAY_NAME = 'InputLabel';

const LabelBase = styled('label', {
  ...labelLegendStyles,
  display: 'flex',
  flexWrap: 'wrap',
});

export const InputLabel = forwardRef<HTMLLabelElement, GDSInputLabelProps>(
  (
    {
      htmlFor,
      hideLabel,
      children,
      required,
      tooltip,
      className,
      css,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    }: GDSInputLabelProps,
    ref: Ref<HTMLLabelElement>,
  ) => {
    const responsiveHideLabel = useResponsiveProp(hideLabel);

    if (responsiveHideLabel)
      return (
        <VisuallyHidden>
          <label htmlFor={htmlFor} ref={ref} {...props}>
            <LabelText required={required}>{children}</LabelText>
          </label>
        </VisuallyHidden>
      );

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <LabelBase
          htmlFor={htmlFor}
          ref={ref}
          className={['GDS-input-label', className].join(' ')}
          css={css}
          data-testid={testId}
          {...props}
        >
          <LabelText required={required}>{children}</LabelText>
          {isValidElement(tooltip) ? tooltip : null}
        </LabelBase>
      </GenesisCoreInspector>
    );
  },
);

InputLabel.displayName = DISPLAY_NAME;
